/**
 * CSV Import Hook
 *
 * Orchestrator hook for the CSV import wizard. Composes sub-hooks for:
 * - Multi-type suggestions (useMultiTypeSuggestions)
 * - Property management (usePropertyManagement)
 * - Import polling (useImportPolling)
 *
 * Pure logic (validation, payload building, hydration) is extracted to
 * csv-import-helpers.ts for independent testing.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAssociationMatchers } from '@/lib/crm/hooks/useAssociationMatchers';
import type {
  AssociationMapping,
  AssociationSuggestion,
  ColumnMappings,
  CrmEntityAssociationMapping,
  CsvImport,
  CsvImportStatus,
  FixedValueMapping,
  MultiTypeMappingSuggestion,
  NewPropertySuggestion,
  SelectOptionSuggestion,
  StageMappings,
  UploadedFileEntry,
} from '@/lib/csv-import/api';
import {
  createCsvImport,
  getCsvImport,
  reanalyzeCsvImport,
} from '@/lib/csv-import/api';
import { logger } from '@/lib/logger';

import type { AssociationTypeSelections } from './csv-import-helpers';
import {
  buildSubmitPayload,
  collapseVirtualFiles,
  hydrateFileData,
  reconstructSuggestionsFromPayload,
  validateMappings,
} from './csv-import-helpers';
import { useImportPolling } from './useImportPolling';
import { useMultiTypeSuggestions } from './useMultiTypeSuggestions';
import { usePropertyManagement } from './usePropertyManagement';

// Re-export for consumers that import from this module
export { SKIP_MARKER } from './csv-import-helpers';

// ============================================================================
// Types
// ============================================================================

export type WizardStep = 1 | 2 | 3;

export interface UseCsvImportState {
  // Wizard navigation
  step: WizardStep;
  // Form state
  importName: string;
  uploadedFiles: UploadedFileEntry[];
  columnMappings: ColumnMappings;
  associationMappings: AssociationMapping[];
  stageMappings: StageMappings;
  /** Per-file, per-column unique-match toggles keyed by file id (or virtual editKey). */
  matchColumns: Record<string, string[]>;
  dirtyTypes: Set<string | number>;
  fileColumns: Record<string, string[]>;
  fileRowCounts: Record<string, number>;
  fileSampleRows: Record<string, Record<string, string>[]>;
  /** Original file objects (or CSV-converted blobs for XLSX) for streaming search. */
  fileObjects: Record<string, File | Blob>;
  suggestedAssociations: Record<string, AssociationSuggestion[]>;
  // Multi-type state (per-file)
  multiTypeSuggestions: Record<string | number, MultiTypeMappingSuggestion>;
  associationTypeSelections: AssociationTypeSelections;
  // User-created new properties per file (keyed by file ID string).
  userNewProperties: Record<string, NewPropertySuggestion[]>;
  // Mappings from CSV columns to existing CRM entities (e.g. owner associations).
  crmEntityAssociationMappings: CrmEntityAssociationMapping[];
  // Per-file fixed association defaults from the "Defaults & fixed values" panel
  // (owner, note visibility), applied to every created entity of the file's type.
  fixedValueMappings: FixedValueMapping[];
  // Import state
  importId: number | null;
  status: CsvImportStatus | null;
  importData: CsvImport | null;
  // UI state
  error: string | null;
  isSubmitting: boolean;
  isPolling: boolean;
  uploadingFileCount: number;
  // Computed state
  isMappingValid: boolean;
  /** Validation errors from the most recent analysis, keyed by file ID then property name. */
  analysisErrorProperties: Record<string, Record<string, string>>;
}

export interface UseCsvImportActions {
  // Form setters
  setImportName: (name: string) => void;
  setUploadedFiles: React.Dispatch<React.SetStateAction<UploadedFileEntry[]>>;
  setColumnMappings: React.Dispatch<React.SetStateAction<ColumnMappings>>;
  setAssociationMappings: (mappings: AssociationMapping[]) => void;
  setStageMappings: React.Dispatch<React.SetStateAction<StageMappings>>;
  setDirtyTypes: React.Dispatch<React.SetStateAction<Set<string | number>>>;
  setFileColumns: React.Dispatch<
    React.SetStateAction<Record<string, string[]>>
  >;
  setFileRowCounts: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  setFileSampleRows: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, string>[]>>
  >;
  setFileObjects: React.Dispatch<
    React.SetStateAction<Record<string, File | Blob>>
  >;
  setSuggestedAssociations: React.Dispatch<
    React.SetStateAction<Record<string, AssociationSuggestion[]>>
  >;
  // Multi-type handlers (per-file)
  handleMultiTypeSuggestionAccepted: (
    fileId: string | number,
    suggestion: MultiTypeMappingSuggestion
  ) => void;
  handleNewPropertyFieldTypeChange: (
    fileId: string | number,
    entityTypeName: string,
    propertyName: string,
    newFieldType: string
  ) => void;
  handleNewPropertyLabelChange: (
    fileId: string | number,
    entityTypeName: string,
    propertyName: string,
    newLabel: string
  ) => void;
  /**
   * Propose a new property for a column — mirrors AI-suggested new_properties
   * but user-initiated. `mappingKey` is the key the originating row reads its
   * column mappings from (plain file id or multi-type virtual editKey); the
   * new column mapping is written under that exact key.
   */
  handleCreateProperty: (
    mappingKey: string | number,
    csvColumn: string,
    label: string
  ) => void;
  /** Update the field type of a user-created new property for a standard (non-multi-type) file. */
  handleUserNewPropertyFieldTypeChange: (
    fileId: string | number,
    propertyName: string,
    entityTypeName: string,
    newFieldType: string
  ) => void;
  /** Update the options of a user-created new property (e.g. from the options editor UI). */
  handleUserNewPropertyOptionsChange: (
    fileId: string | number,
    propertyName: string,
    entityTypeName: string,
    options: SelectOptionSuggestion[]
  ) => void;
  /** Update the label of a user-created new property (e.g. from the New Properties panel). */
  handleUserNewPropertyLabelChange: (
    fileId: string | number,
    propertyName: string,
    entityTypeName: string,
    newLabel: string
  ) => void;
  /**
   * Regenerate a select property's options from the CSV ("Generate options from
   * CSV"). Shares the options generation guard so a late response can't clobber
   * a newer user edit.
   */
  handleGenerateSelectOptions: (
    fileId: string | number,
    propertyName: string,
    entityTypeName: string
  ) => void;
  setCrmEntityAssociationMappings: (
    mappings: CrmEntityAssociationMapping[]
  ) => void;
  setFixedValueMappings: React.Dispatch<
    React.SetStateAction<FixedValueMapping[]>
  >;
  clearMultiTypeModeForFile: (fileId: string | number) => void;
  clearMultiTypeMode: () => void;
  setAssociationTypeSelection: (
    fileId: string | number,
    sourceType: string,
    targetType: string,
    associationTypeName: string
  ) => void;
  addEntityTypeToFile: (
    fileId: string | number,
    entityTypeName: string
  ) => void;
  setUploadingFileCount: React.Dispatch<React.SetStateAction<number>>;
  /** Toggle whether a CSV column participates in unique-match lookups for a file. */
  handleMatchColumnToggle: (
    fileId: string | number,
    csvColumn: string,
    enabled: boolean
  ) => void;
  /** Seed the initial unique-match columns for a file (or virtual editKey), e.g. when a mapping suggestion is accepted. A no-op for an empty list. */
  seedMatchColumns: (editKey: string, columns: string[]) => void;
  // Navigation
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: WizardStep) => void;
  // Actions
  submit: () => Promise<void>;
  reset: () => void;
  clearError: () => void;
}

export type UseCsvImportReturn = UseCsvImportState & UseCsvImportActions;

// ============================================================================
// Hook
// ============================================================================

/**
 * Orchestrator hook for CSV import wizard state.
 *
 * Composes sub-hooks for polling, multi-type suggestions, and property
 * management. Pure logic (validation, payload building, hydration) lives
 * in csv-import-helpers.ts.
 *
 * @param orgId - Organization ID for API calls
 * @param resumeImportId - Optional import ID to resume editing
 */
export function useCsvImport(
  orgId: number,
  resumeImportId?: number | null
): UseCsvImportReturn {
  // -------------------------------------------------------------------------
  // Core form state
  // -------------------------------------------------------------------------
  const [step, setStep] = useState<WizardStep>(1);
  const [importName, setImportName] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileEntry[]>([]);
  const [columnMappings, setColumnMappings] = useState<ColumnMappings>({});
  const [associationMappings, setAssociationMappings] = useState<
    AssociationMapping[]
  >([]);
  const [stageMappings, setStageMappings] = useState<StageMappings>({});
  const [matchColumns, setMatchColumns] = useState<Record<string, string[]>>(
    {}
  );
  const [dirtyTypes, setDirtyTypes] = useState(
    () => new Set<string | number>()
  );
  const [crmEntityAssociationMappings, setCrmEntityAssociationMappings] =
    useState<CrmEntityAssociationMapping[]>([]);
  const [fixedValueMappings, setFixedValueMappings] = useState<
    FixedValueMapping[]
  >([]);
  const [uploadingFileCount, setUploadingFileCount] = useState(0);

  // -------------------------------------------------------------------------
  // File metadata (populated during hydration from analysis results)
  // -------------------------------------------------------------------------
  const [fileColumns, setFileColumns] = useState<Record<string, string[]>>({});
  const [fileRowCounts, setFileRowCounts] = useState<Record<string, number>>(
    {}
  );
  const [fileSampleRows, setFileSampleRows] = useState<
    Record<string, Record<string, string>[]>
  >({});
  const [fileObjects, setFileObjects] = useState<Record<string, File | Blob>>(
    {}
  );
  const [suggestedAssociations, setSuggestedAssociations] = useState<
    Record<string, AssociationSuggestion[]>
  >({});
  const [analysisErrorProperties, setAnalysisErrorProperties] = useState<
    Record<string, Record<string, string>>
  >({});

  // -------------------------------------------------------------------------
  // Import state (tracks the backend import record)
  // -------------------------------------------------------------------------
  const [importId, setImportId] = useState<number | null>(null);
  const [status, setStatus] = useState<CsvImportStatus | null>(null);
  const [importData, setImportData] = useState<CsvImport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // -------------------------------------------------------------------------
  // Sub-hooks
  // -------------------------------------------------------------------------
  const {
    multiTypeSuggestions,
    setMultiTypeSuggestions,
    associationTypeSelections,
    setAssociationTypeSelections,
    handleMultiTypeSuggestionAccepted,
    handleNewPropertyFieldTypeChange,
    handleNewPropertyLabelChange,
    clearMultiTypeModeForFile,
    clearMultiTypeMode,
    setAssociationTypeSelection,
    addEntityTypeToFile,
  } = useMultiTypeSuggestions(
    setColumnMappings,
    setStageMappings,
    setAssociationMappings
  );

  // Association types + kind-aware matchers — used to compute the valid
  // within-file associations and serialize the user's selections.
  const {
    associationTypes,
    matchesSource,
    matchesTarget,
    isLoading: associationMatchersLoading,
  } = useAssociationMatchers(orgId);

  const {
    userNewProperties,
    setUserNewProperties,
    handleCreateProperty,
    handleUserNewPropertyFieldTypeChange,
    handleUserNewPropertyOptionsChange,
    handleUserNewPropertyLabelChange,
    handleGenerateSelectOptions,
  } = usePropertyManagement(
    uploadedFiles,
    setColumnMappings,
    orgId,
    multiTypeSuggestions
  );

  // A new property's field type lives in TWO places: the multi-type suggestion
  // (what the mapping table's field-type select renders) and userNewProperties
  // (what the New Properties panel renders, and what the submit payload
  // prefers — see collectFilesAndMappings in csv-import-helpers). Writing only
  // the suggestion left the panel showing the pre-edit type with no options,
  // and imported the property as that stale type. Write both.
  //
  // The user-property handler is also what fetches suggested options when the
  // type becomes 'select', so this is what pre-populates the options the
  // "Configure options →" link sends the user to configure.
  const handleTableNewPropertyFieldTypeChange = useCallback(
    (
      fileId: string | number,
      entityTypeName: string,
      propertyName: string,
      newFieldType: string
    ) => {
      handleNewPropertyFieldTypeChange(
        fileId,
        entityTypeName,
        propertyName,
        newFieldType
      );
      handleUserNewPropertyFieldTypeChange(
        fileId,
        propertyName,
        entityTypeName,
        newFieldType
      );
    },
    [handleNewPropertyFieldTypeChange, handleUserNewPropertyFieldTypeChange]
  );

  // Prune orphaned new properties: a user-created "new property" should only
  // exist while at least one column maps to it. When the user changes that
  // column to "Do not import", to an existing property, or to link mode, the
  // new property must stop being shown and stop being created on import.
  // handleCreateProperty sets columnMappings + userNewProperties in one handler
  // (batched), so a newly-created property is always referenced by the time this
  // runs and is never pruned. We only ever drop a property referenced by NO
  // column under any key belonging to its file (real or virtual multi-type keys),
  // so this can never delete a property the user is still mapping to.
  useEffect(() => {
    setUserNewProperties(prev => {
      let changed = false;
      const next: Record<string, NewPropertySuggestion[]> = {};
      for (const [fileId, props] of Object.entries(prev)) {
        const referenced = new Set<string>();
        for (const [mapKey, cols] of Object.entries(columnMappings)) {
          if (mapKey === fileId || mapKey.startsWith(`${fileId}_`)) {
            for (const value of Object.values(cols)) {
              if (value) {
                referenced.add(value);
              }
            }
          }
        }
        const kept = props.filter(p => referenced.has(p.property_name));
        if (kept.length !== props.length) {
          changed = true;
        }
        if (kept.length > 0) {
          next[fileId] = kept;
        }
      }
      return changed ? next : prev;
    });
  }, [columnMappings, setUserNewProperties]);

  // Polling callbacks — stable references that update state
  const handlePollData = useCallback((data: CsvImport) => {
    setImportData(data);
    setStatus(data.status);
  }, []);

  const handlePollError = useCallback((message: string) => {
    setError(message);
    setIsSubmitting(false);
  }, []);

  const { isPolling, startPolling, resetPolling } = useImportPolling(
    orgId,
    importId,
    handlePollData,
    handlePollError
  );

  // -------------------------------------------------------------------------
  // Resume mode: hydrate state from an existing import
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!resumeImportId) {
      return;
    }
    let cancelled = false;
    const hydrate = async () => {
      try {
        const data = await getCsvImport(orgId, resumeImportId);
        if (cancelled) {
          return;
        }
        // Rebuild multi-type sections from saved payload (fixes resume bug
        // where virtual file IDs use backend {id}_{type} format but the UI
        // reads editKeys in {id}_virtual_{type} format).
        const reconstructed = reconstructSuggestionsFromPayload(data);

        setImportId(data.id);
        setImportName(data.name);
        // Collapse virtual files (e.g. {file_id: "22_ahoy_contact"}) back
        // into source files ({file_id: 22}) so uploadedFiles keys match
        // multiTypeSuggestions keys (keyed by source_file_id).
        setUploadedFiles(collapseVirtualFiles(data.uploaded_files));
        // Merge: standard-file entries from backend + editKey-format virtual entries
        setColumnMappings({
          ...data.column_mappings,
          ...reconstructed.columnMappings,
        });
        setStageMappings({
          ...(data.stage_mappings ?? {}),
          ...reconstructed.stageMappings,
        });
        setMatchColumns(reconstructed.matchColumns);
        // Only cross-file (user-defined) associations are restored here.
        // Within-file associations are regenerated from associationSelections at
        // submit time (processMultiTypeFile); carrying them here too would
        // duplicate them in the payload.
        setAssociationMappings(reconstructed.crossFileAssociationMappings);
        // Use reconstructed version: stage/link association mappings re-keyed
        // from backend {id}_{type} to editKey {id}_virtual_{type} format so the
        // mapping UI and preview match them on resume (same re-key as
        // fixedValueMappings).
        setCrmEntityAssociationMappings(
          reconstructed.crmEntityAssociationMappings
        );
        // Use reconstructed version: virtual entries re-keyed to editKey format
        setFixedValueMappings(reconstructed.fixedValueMappings);
        setStatus(data.status);
        setImportData(data);
        setMultiTypeSuggestions(reconstructed.suggestions);
        setAssociationTypeSelections(reconstructed.associationSelections);

        // If still analyzing, kick off polling
        if (data.status === 'analyzing') {
          startPolling();
        }

        // Extract file metadata from analysis results
        const hydrated = hydrateFileData(data);
        setFileColumns(hydrated.fileColumns);
        setFileRowCounts(hydrated.fileRowCounts);
        setFileSampleRows(hydrated.fileSampleRows);
        setAnalysisErrorProperties(hydrated.analysisErrorProperties);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load import:', err);
          setError("We couldn't load this import. Try again.");
        }
      }
    };
    hydrate();
    return () => {
      cancelled = true;
    };
  }, [
    resumeImportId,
    orgId,
    startPolling,
    setMultiTypeSuggestions,
    setAssociationTypeSelections,
  ]);

  // -------------------------------------------------------------------------
  // Computed state
  // -------------------------------------------------------------------------
  const isMappingValid = useMemo(
    () =>
      validateMappings(
        uploadedFiles,
        columnMappings,
        associationMappings,
        stageMappings,
        multiTypeSuggestions,
        userNewProperties,
        crmEntityAssociationMappings
      ),
    [
      uploadedFiles,
      columnMappings,
      associationMappings,
      stageMappings,
      multiTypeSuggestions,
      userNewProperties,
      crmEntityAssociationMappings,
    ]
  );

  // -------------------------------------------------------------------------
  // Navigation
  // -------------------------------------------------------------------------
  const nextStep = useCallback(() => {
    setStep(current => {
      if (current < 3) {
        return (current + 1) as WizardStep;
      }
      return current;
    });
  }, []);

  const prevStep = useCallback(() => {
    setStep(current => {
      if (current > 1) {
        return (current - 1) as WizardStep;
      }
      return current;
    });
  }, []);

  const goToStep = useCallback((newStep: WizardStep) => {
    setStep(newStep);
  }, []);

  // -------------------------------------------------------------------------
  // Per-column unique-match toggles
  // -------------------------------------------------------------------------
  const handleMatchColumnToggle = useCallback(
    (fileId: string | number, csvColumn: string, enabled: boolean) => {
      const key = String(fileId);
      setMatchColumns(prev => {
        const current = prev[key] ?? [];
        const has = current.includes(csvColumn);
        if (enabled === has) {
          return prev;
        }
        const nextCols = enabled
          ? [...current, csvColumn]
          : current.filter(c => c !== csvColumn);
        const next = { ...prev };
        if (nextCols.length > 0) {
          next[key] = nextCols;
        } else {
          Reflect.deleteProperty(next, key);
        }
        return next;
      });
    },
    []
  );

  const seedMatchColumns = useCallback((editKey: string, columns: string[]) => {
    setMatchColumns(prev => {
      if (columns.length === 0) {
        return prev;
      }
      return { ...prev, [editKey]: columns };
    });
  }, []);

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------
  const submit = useCallback(async () => {
    setError(null);

    // The association matchers feed buildSubmitPayload's inference of within-file
    // (section-to-section) associations. If their data hasn't loaded yet, building
    // the payload now would silently drop every inferred association. Block submit
    // with a user-visible message until the matcher data is ready.
    if (associationMatchersLoading) {
      setError(
        'Association data is still loading. Please try again in a moment.'
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = buildSubmitPayload(
        importName,
        uploadedFiles,
        columnMappings,
        associationMappings,
        stageMappings,
        crmEntityAssociationMappings,
        multiTypeSuggestions,
        associationTypeSelections,
        userNewProperties,
        fixedValueMappings,
        associationTypes,
        matchesSource,
        matchesTarget,
        matchColumns
      );

      // Create or reanalyze
      const data = importId
        ? await reanalyzeCsvImport(orgId, importId, payload)
        : await createCsvImport(orgId, payload);

      setImportId(data.id);
      setStatus(data.status);
      setImportData(data);

      // Start polling if backend is analyzing
      if (data.status === 'analyzing') {
        startPolling();
      }
    } catch (err) {
      logger.error(
        'Failed to submit import',
        err instanceof Error ? err : new Error(String(err)),
        { org_id: orgId, file_count: uploadedFiles.length }
      );
      setError("We couldn't create your import. Try again.");
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    orgId,
    importId,
    importName,
    uploadedFiles,
    columnMappings,
    associationMappings,
    stageMappings,
    crmEntityAssociationMappings,
    multiTypeSuggestions,
    associationTypeSelections,
    userNewProperties,
    fixedValueMappings,
    associationTypes,
    matchesSource,
    matchesTarget,
    matchColumns,
    associationMatchersLoading,
    startPolling,
  ]);

  // -------------------------------------------------------------------------
  // Reset
  // -------------------------------------------------------------------------
  const reset = useCallback(() => {
    setStep(1);
    setImportName('');
    setUploadedFiles([]);
    setColumnMappings({});
    setAssociationMappings([]);
    setStageMappings({});
    setMatchColumns({});
    setDirtyTypes(new Set());
    setImportId(null);
    setStatus(null);
    setImportData(null);
    setError(null);
    setIsSubmitting(false);
    setFileColumns({});
    setFileRowCounts({});
    setFileSampleRows({});
    setFileObjects({});
    setSuggestedAssociations({});
    setUserNewProperties({});
    setCrmEntityAssociationMappings([]);
    setFixedValueMappings([]);
    setUploadingFileCount(0);
    setAnalysisErrorProperties({});
    clearMultiTypeMode();
    resetPolling();
  }, [setUserNewProperties, clearMultiTypeMode, resetPolling]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // -------------------------------------------------------------------------
  // Return composed state + actions
  // -------------------------------------------------------------------------
  return {
    // State
    step,
    importName,
    uploadedFiles,
    columnMappings,
    associationMappings,
    stageMappings,
    matchColumns,
    dirtyTypes,
    fileColumns,
    fileRowCounts,
    fileSampleRows,
    fileObjects,
    suggestedAssociations,
    multiTypeSuggestions,
    associationTypeSelections,
    userNewProperties,
    crmEntityAssociationMappings,
    fixedValueMappings,
    importId,
    status,
    importData,
    error,
    isSubmitting,
    isPolling,
    uploadingFileCount,
    isMappingValid,
    analysisErrorProperties,
    // Actions
    setImportName,
    setUploadedFiles,
    setColumnMappings,
    setAssociationMappings,
    setStageMappings,
    setDirtyTypes,
    setFileColumns,
    setFileRowCounts,
    setFileSampleRows,
    setFileObjects,
    setSuggestedAssociations,
    handleMultiTypeSuggestionAccepted,
    handleNewPropertyFieldTypeChange: handleTableNewPropertyFieldTypeChange,
    handleNewPropertyLabelChange,
    handleCreateProperty,
    handleUserNewPropertyFieldTypeChange,
    handleUserNewPropertyOptionsChange,
    handleUserNewPropertyLabelChange,
    handleGenerateSelectOptions,
    setCrmEntityAssociationMappings,
    setFixedValueMappings,
    clearMultiTypeModeForFile,
    clearMultiTypeMode,
    setAssociationTypeSelection,
    addEntityTypeToFile,
    setUploadingFileCount,
    handleMatchColumnToggle,
    seedMatchColumns,
    nextStep,
    prevStep,
    goToStep,
    submit,
    reset,
    clearError,
  };
}
